import type {
  WorkbookDefinition,
  WorkbookDiagnostic,
  WorkbookRunMode,
  WorkbookRunStatus,
} from "@erp/shared-types";
import { describeDiagnostic } from "./diagnostics";
import { workbookDependencies, workbookExecutionOrder } from "./executionOrder";
import { sheetFingerprints, sourceFingerprint } from "./fingerprint";
import { findOutput, outputSheetIds } from "./outputs";

export const RUN_MODES = [
  "entire",
  "up-to",
  "from",
  "only",
  "sample",
] as const satisfies readonly WorkbookRunMode[];

/** What a Run in one mode computes, and what it reads without computing. */
export interface RunScope {
  mode: WorkbookRunMode;
  /**
   * The Target: the Worksheet an `up-to`, `from` or `only` Run is started on, or the
   * Output an `up-to` Run is started on. Null for `entire`, or when it names
   * nothing the mode can start on.
   */
  targetId: string | null;
  /** Worksheets the Run computes, in Execution order. */
  computeSheetIds: string[];
  /** Sources the computed Worksheets read: every Source for `entire` and `sample`. */
  sourceIds: string[];
  /** Worksheets the computed ones read without computing: their Results come from the Base run. Only `from` and `only` have any. */
  baseSheetIds: string[];
}

/** What choosing a Base run needs from a listed Run. */
export interface RunRecord {
  id: string;
  status: WorkbookRunStatus;
  /** A Sampled run (`sample`) is never a Base run. Absent reads as a full Run. */
  mode?: WorkbookRunMode;
  results: readonly { sheetId: string; fingerprint: string }[];
  sources: readonly {
    sourceId: string;
    fingerprint: string;
    dataVersion?: string | null;
  }[];
}

/** Why a Run mode cannot start. */
export type RunModeBlocker =
  | { code: "no-worksheets" }
  | { code: "no-target" }
  /** A Sampled run needs the Definition's `sampling`. */
  | { code: "no-sampling" }
  | { code: "diagnostic"; diagnostic: WorkbookDiagnostic }
  | { code: "no-base-run" }
  /** The newest completed Run has no Result (or Snapshot) for this upstream Item. */
  | { code: "not-in-base-run"; itemId: string }
  /** This upstream Item changed since the newest completed Run. */
  | { code: "changed-since-base-run"; itemId: string };

export interface RunModeOption {
  mode: WorkbookRunMode;
  allowed: boolean;
  blocker: RunModeBlocker | null;
  /** The Run a `from` or `only` Run reuses; null for the other modes. */
  baseRunId: string | null;
  scope: RunScope;
}

function closure(
  start: string,
  next: (id: string) => readonly string[],
): Set<string> {
  const seen = new Set<string>([start]);
  const stack = [start];
  for (let id = stack.pop(); id !== undefined; id = stack.pop())
    for (const other of next(id))
      if (!seen.has(other)) {
        seen.add(other);
        stack.push(other);
      }
  return seen;
}

/**
 * The Worksheets a Target stands for in `mode`: a Worksheet itself; for `up-to`,
 * an Output's Worksheets (running up to an Output runs up to what it reads).
 */
function targetSheetIds(
  definition: WorkbookDefinition,
  mode: WorkbookRunMode,
  targetId: string | null,
): string[] {
  if (!targetId) return [];
  if (definition.sheets.some((sheet) => sheet.id === targetId))
    return [targetId];
  const output =
    mode === "up-to" ? findOutput(definition, targetId) : undefined;
  return output ? outputSheetIds(definition, output) : [];
}

/**
 * The Worksheets and Sources a Run in `mode` covers. `up-to` computes the Target's
 * Worksheets and everything they read, transitively; `from` computes the target
 * Worksheet and everything that reads it, transitively, and reads the rest from the
 * Base run; `only` computes the target Worksheet alone and reads everything it reads
 * from the Base run, leaving the Worksheets downstream of it as they were.
 */
export function runScope(
  definition: WorkbookDefinition,
  mode: WorkbookRunMode,
  targetId: string | null,
): RunScope {
  const sheets = new Map(definition.sheets.map((sheet) => [sheet.id, sheet]));
  const sourceIds = new Set(definition.sources.map((source) => source.id));
  const dependencies = workbookDependencies(definition);
  const reads = (id: string) => dependencies.get(id) ?? [];
  const targets = targetSheetIds(definition, mode, targetId);
  const readers = (id: string) =>
    definition.sheets
      .filter((sheet) => reads(sheet.id).includes(id))
      .map((sheet) => sheet.id);
  const whole = mode === "entire" || mode === "sample";
  let computed: Set<string>;
  if (whole) computed = new Set(sheets.keys());
  else if (mode === "up-to")
    computed = new Set(
      targets
        .flatMap((target) => [...closure(target, reads)])
        .filter((id) => sheets.has(id)),
    );
  else if (mode === "only") computed = new Set(targets);
  else
    computed = new Set(
      targets.flatMap((target) => [...closure(target, readers)]),
    );
  const order = workbookExecutionOrder(definition)
    .filter((item) => item.kind === "sheet")
    .map((item) => item.id);
  const computeSheetIds = order.filter((id) => computed.has(id));
  const external = new Set(
    computeSheetIds.flatMap(reads).filter((id) => !computed.has(id)),
  );
  return {
    mode,
    targetId: whole || !targets.length ? null : targetId,
    computeSheetIds,
    sourceIds: whole
      ? [...sourceIds]
      : definition.sources
          .map((source) => source.id)
          .filter((id) => external.has(id)),
    baseSheetIds: order.filter((id) => external.has(id) && sheets.has(id)),
  };
}

interface BaseRequirement {
  id: string;
  /** Whether the Run holds this Item at all. */
  holds: (run: RunRecord) => boolean;
  /** Whether the Run holds it unchanged in the edited Definition. */
  matches: (run: RunRecord) => boolean;
}

/**
 * Each Item a `from` or `only` Run reads without computing. A Source matches when its Definition
 * is unchanged: its Fingerprint is re-derived with the Data version the Run recorded,
 * since whether the data changed is the server's call when the Run starts (it reads a
 * Source whose data changed fresh instead of reusing the Base run's Snapshot).
 */
function baseRequirements(
  definition: WorkbookDefinition,
  scope: RunScope,
): BaseRequirement[] {
  const sheets = sheetFingerprints(definition);
  const sources = new Map(
    definition.sources.map((source) => [source.id, source]),
  );
  return [
    ...scope.baseSheetIds.map((id) => {
      const fingerprint = sheets.get(id);
      const recorded = (run: RunRecord) =>
        run.results.find((result) => result.sheetId === id);
      return {
        id,
        holds: (run: RunRecord) => recorded(run) !== undefined,
        matches: (run: RunRecord) =>
          fingerprint !== undefined &&
          recorded(run)?.fingerprint === fingerprint,
      };
    }),
    ...scope.sourceIds.map((id) => {
      const source = sources.get(id);
      const recorded = (run: RunRecord) =>
        run.sources.find((item) => item.sourceId === id);
      return {
        id,
        holds: (run: RunRecord) => recorded(run) !== undefined,
        matches: (run: RunRecord) => {
          const record = recorded(run);
          return (
            source !== undefined &&
            record !== undefined &&
            sourceFingerprint(source, record.dataVersion) === record.fingerprint
          );
        },
      };
    }),
  ];
}

/**
 * The Base run for a `from` or `only` Run: the newest completed Run holding a Current Result for
 * every upstream Worksheet the scope reads and the same Source for every Source it
 * reads — nothing upstream changed since it. Otherwise, what the newest completed Run lacks.
 */
export function baseRunFor<Run extends RunRecord>(
  definition: WorkbookDefinition,
  scope: RunScope,
  runs: readonly Run[],
): { run: Run; blocker: null } | { run: null; blocker: RunModeBlocker } {
  const requirements = baseRequirements(definition, scope);
  // A Sampled run computed over a subset: nothing builds on it.
  const completed = runs.filter(
    (run) => run.status === "completed" && run.mode !== "sample",
  );
  const run = completed.find((candidate) =>
    requirements.every((requirement) => requirement.matches(candidate)),
  );
  if (run) return { run, blocker: null };
  const [newest] = completed;
  if (!newest) return { run: null, blocker: { code: "no-base-run" } };
  const unmet =
    requirements.find((requirement) => !requirement.matches(newest)) ??
    requirements[0];
  if (!unmet) return { run: null, blocker: { code: "no-base-run" } };
  return {
    run: null,
    blocker: unmet.holds(newest)
      ? { code: "changed-since-base-run", itemId: unmet.id }
      : { code: "not-in-base-run", itemId: unmet.id },
  };
}

/**
 * Whether a `from` or `only` Run reuses its Base run's Snapshot of a Source instead of reading
 * the Source fresh: only when the Source's Data version now is the one the Base run
 * recorded. A Source without a Data version (null) is never proven unchanged.
 */
export function reusesBaseSnapshot(
  base: RunRecord,
  sourceId: string,
  dataVersion: string | null,
): boolean {
  return (
    dataVersion !== null &&
    base.sources.some(
      (source) =>
        source.sourceId === sourceId && source.dataVersion === dataVersion,
    )
  );
}

/** The first Diagnostic located on an Item the scope computes or reads fresh; `entire` and `sample` count every one. */
function scopeDiagnostic(
  diagnostics: readonly WorkbookDiagnostic[],
  scope: RunScope,
): WorkbookDiagnostic | undefined {
  if (scope.mode === "entire" || scope.mode === "sample") return diagnostics[0];
  const items = new Set([...scope.computeSheetIds, ...scope.sourceIds]);
  return diagnostics.find((diagnostic) =>
    items.has(diagnostic.location.itemId),
  );
}

/**
 * Whether only a Base run would let `from` start: none yet, or none holding
 * what the target reads now. A Live preview then takes a first look instead,
 * over the first rows of each Source.
 */
export function lacksBaseRun(blocker: RunModeBlocker | null): boolean {
  return (
    blocker?.code === "no-base-run" ||
    blocker?.code === "not-in-base-run" ||
    blocker?.code === "changed-since-base-run"
  );
}

/** Whether a Run in `mode` reads what it does not compute from a Base run: `from` and `only`. */
export function reusesBaseRun(mode: WorkbookRunMode): boolean {
  return mode === "from" || mode === "only";
}

export interface RunModeInput<Run extends RunRecord> {
  definition: WorkbookDefinition;
  /** The Plan's Diagnostics for `definition`. */
  diagnostics: readonly WorkbookDiagnostic[];
  /** Listed Runs, newest first. */
  runs: readonly Run[];
}

/**
 * Whether a Run in `mode` on the Target `targetId` can start. The editor shows it and the
 * server re-checks it with the same function before it starts a Run or a Live preview.
 * - `entire`: no Diagnostics anywhere.
 * - `up-to`: no Diagnostics on the Target's Worksheets, their upstream Worksheets or the Sources they read.
 * - `from`: no Diagnostics on the target or downstream, and a Base run nothing upstream changed since.
 * - `only`: no Diagnostics on the target, and a Base run nothing upstream changed since.
 * - `sample`: no Diagnostics anywhere, and a `sampling`.
 */
export function runModeOption<Run extends RunRecord>(
  input: RunModeInput<Run>,
  mode: WorkbookRunMode,
  targetId: string | null,
): RunModeOption {
  const scope = runScope(input.definition, mode, targetId);
  const blocked = (blocker: RunModeBlocker): RunModeOption => ({
    mode,
    allowed: false,
    blocker,
    baseRunId: null,
    scope,
  });
  if (!input.definition.sheets.length)
    return blocked({ code: "no-worksheets" });
  if (mode !== "entire" && mode !== "sample" && !scope.targetId)
    return blocked({ code: "no-target" });
  if (mode === "sample" && !input.definition.sampling)
    return blocked({ code: "no-sampling" });
  const diagnostic = scopeDiagnostic(input.diagnostics, scope);
  if (diagnostic) return blocked({ code: "diagnostic", diagnostic });
  if (!reusesBaseRun(mode))
    return { mode, allowed: true, blocker: null, baseRunId: null, scope };
  const base = baseRunFor(input.definition, scope, input.runs);
  if (base.blocker) return blocked(base.blocker);
  return { mode, allowed: true, blocker: null, baseRunId: base.run.id, scope };
}

/** Every Run mode's option for one Target: a Worksheet, or an Output (which only `up-to` can start on). */
export function runModeOptions<Run extends RunRecord>(
  input: RunModeInput<Run>,
  targetId: string | null,
): Record<WorkbookRunMode, RunModeOption> {
  return {
    entire: runModeOption(input, "entire", targetId),
    "up-to": runModeOption(input, "up-to", targetId),
    from: runModeOption(input, "from", targetId),
    only: runModeOption(input, "only", targetId),
    sample: runModeOption(input, "sample", targetId),
  };
}

function itemName(definition: WorkbookDefinition, itemId: string): string {
  return (
    [...definition.sources, ...definition.sheets].find(
      (item) => item.id === itemId,
    )?.name ?? "An upstream item"
  );
}

/** One sentence saying why a Run mode cannot start, for server errors and the agent. */
export function describeRunModeBlocker(
  definition: WorkbookDefinition,
  blocker: RunModeBlocker,
): string {
  switch (blocker.code) {
    case "no-worksheets":
      return "Add a worksheet before running.";
    case "no-target":
      return "Choose a worksheet, or an output that reads one, to run.";
    case "no-sampling":
      return "Choose a sampling key before running on a sample.";
    case "diagnostic":
      return `Fix the workbook before running. ${describeDiagnostic(definition, blocker.diagnostic)}`;
    case "no-base-run":
      return "Run the workbook first: running from or only a worksheet reuses an earlier Run's inputs.";
    case "not-in-base-run":
      return `'${itemName(definition, blocker.itemId)}' has no result in the last run. Run through this worksheet first.`;
    case "changed-since-base-run":
      return `'${itemName(definition, blocker.itemId)}' changed since the last run. Run through this worksheet first.`;
  }
}
